import {
  entrySteps,
  graphIds,
  isLeaf,
  type AgentDto,
  type Condition,
  type ConditionOperator,
  type SandboxDto,
  type ToolDto,
  type WorkflowContainerStep,
  type WorkflowDefinition,
  type WorkflowDto,
  type WorkflowEntry,
  type WorkflowEntryType,
  type WorkflowEventDto,
  type WorkflowRunStatus,
  type WorkflowStep,
  type WorkflowStepResult,
  type WorkflowStepStatus,
} from '@agent-platform/shared';
import {
  Bot,
  Clock,
  Columns3,
  GitBranch,
  Hand,
  Repeat,
  Rows3,
  Shuffle,
  Terminal,
  Workflow,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export const ENTRY_META: Record<
  WorkflowEntryType,
  { label: string; icon: LucideIcon; hint: string }
> = {
  agent: { label: 'Agent', icon: Bot, hint: 'Ask an agent; its answer is the output' },
  tool: { label: 'Tool', icon: Wrench, hint: 'Call an HTTP, CLI or sandbox tool' },
  sandbox: { label: 'Sandbox', icon: Terminal, hint: 'Run a fixed program on the step data' },
  transform: { label: 'Transform', icon: Shuffle, hint: 'Reshape values with a template' },
  workflow: { label: 'Workflow', icon: Workflow, hint: 'Run another workflow as one step' },
  human: { label: 'Human input', icon: Hand, hint: 'Suspend until a person responds' },
  sleep: { label: 'Sleep', icon: Clock, hint: 'Wait for a time or until a date' },
  parallel: { label: 'Parallel', icon: Columns3, hint: 'Run steps at the same time' },
  conditional: { label: 'Branch', icon: GitBranch, hint: 'Run the steps whose condition holds' },
  loop: { label: 'Loop', icon: Repeat, hint: 'Repeat a step while / until a condition' },
  foreach: { label: 'For each', icon: Rows3, hint: 'Run a step for every item of a list' },
};

/** The order of the "Add step" menu. */
export const LEAF_TYPES = ['agent', 'tool', 'sandbox', 'transform', 'workflow', 'human'] as const;
export const CONTAINER_ENTRY_TYPES = [
  'parallel',
  'conditional',
  'loop',
  'foreach',
  'sleep',
] as const;
/** Steps allowed inside containers. */
export const INNER_TYPES = ['agent', 'tool', 'sandbox', 'transform', 'workflow'] as const;

export const WORKFLOW_STATUS_VARIANT: Record<
  WorkflowRunStatus,
  'outline' | 'secondary' | 'destructive' | 'default'
> = {
  running: 'outline',
  succeeded: 'secondary',
  failed: 'destructive',
  cancelled: 'outline',
  suspended: 'default',
  waiting: 'outline',
  paused: 'default',
};

export const WORKFLOW_STATUS_LABEL: Record<WorkflowRunStatus, string> = {
  running: 'Running',
  succeeded: 'Succeeded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  suspended: 'Needs input',
  waiting: 'Sleeping',
  paused: 'Paused',
};

export const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  is_empty: 'is empty',
  not_empty: 'is not empty',
  greater_than: '>',
  greater_or_equal: '≥',
  less_than: '<',
  less_or_equal: '≤',
};
export const UNARY_OPERATORS: ConditionOperator[] = ['is_empty', 'not_empty'];

export interface WorkflowResources {
  agents: AgentDto[];
  tools: ToolDto[];
  sandboxes: SandboxDto[];
  workflows: WorkflowDto[];
}

/** Sandbox code is a fixed program; workflow values arrive in input.json. */
export const SANDBOX_STARTER: Record<SandboxDto['runtime'], string> = {
  python:
    'import json\n\ndata = json.load(open("input.json"))  # {"input", "context", "steps", "prev", "item"}\nprint(json.dumps({"received": list(data["input"].keys())}))\n',
  node: 'import { readFileSync } from "node:fs";\n\nconst data = JSON.parse(readFileSync("input.json", "utf8")); // { input, context, steps, prev, item }\nconsole.log(JSON.stringify({ received: Object.keys(data.input) }));\n',
  bash: '# Workflow values are in input.json: {"input", "context", "steps", "prev", "item"}\ncat input.json\n',
};

/** An id of the form `<type>_<n>` not used anywhere in the graph. */
export function freshId(def: WorkflowDefinition, base: string, taken: string[] = []): string {
  const used = new Set([...graphIds(def.graph), ...taken]);
  let n = 1;
  while (used.has(`${base}_${n}`)) n++;
  return `${base}_${n}`;
}

const condition = (left: string): Condition => ({
  match: 'all',
  rules: [{ left, operator: 'not_empty', right: '' }],
});

export function newLeaf(
  type: WorkflowStep['type'],
  def: WorkflowDefinition,
  r: WorkflowResources,
  taken: string[] = [],
  selfId?: string,
): WorkflowStep {
  const id = freshId(def, type, taken);
  switch (type) {
    case 'agent':
      return {
        id,
        name: 'Ask an agent',
        type,
        agentId: r.agents[0]?.id ?? '',
        version: 'published',
        prompt: def.graph.length > 0 ? '{{prev}}' : 'Hello!',
        output: 'text',
      };
    case 'tool':
      return { id, name: 'Call a tool', type, toolId: r.tools[0]?.id ?? '', args: {} };
    case 'sandbox':
      return {
        id,
        name: 'Run code',
        type,
        sandboxId: r.sandboxes[0]?.id ?? '',
        code: SANDBOX_STARTER[r.sandboxes[0]?.runtime ?? 'python'],
      };
    case 'transform':
      return { id, name: 'Transform', type, output: def.graph.length > 0 ? '{{prev}}' : '' };
    case 'workflow':
      return {
        id,
        name: 'Run a workflow',
        type,
        workflowId: r.workflows.find((w) => w.id !== selfId)?.id ?? '',
        input: {},
      };
    case 'human':
      return {
        id,
        name: 'Ask for approval',
        type,
        message: 'Please review and approve.',
        fields: [{ name: 'approved', type: 'boolean', required: true, description: '' }],
      };
  }
}

export function newEntry(
  type: WorkflowEntryType,
  def: WorkflowDefinition,
  r: WorkflowResources,
  selfId?: string,
): WorkflowEntry {
  if ((LEAF_TYPES as readonly string[]).includes(type))
    return newLeaf(type as WorkflowStep['type'], def, r, [], selfId);
  const id = freshId(def, type);
  const inner = (output: string, taken: string[] = []): WorkflowContainerStep => ({
    id: freshId(def, 'transform', [id, ...taken]),
    name: 'Transform',
    type: 'transform',
    output,
  });
  switch (type) {
    case 'sleep':
      return { id, name: 'Wait', type, mode: 'duration', durationMs: 60_000 };
    case 'parallel': {
      const a = inner('{{prev}}');
      const b = inner('{{prev}}', [a.id]);
      return { id, name: 'In parallel', type, steps: [a, b] };
    }
    case 'conditional': {
      const a = inner('{{prev}}');
      return {
        id,
        name: 'Branch',
        type,
        branches: [{ condition: condition('{{prev}}'), step: a }],
      };
    }
    case 'loop': {
      const a = inner('attempt {{iteration}}');
      return {
        id,
        name: 'Repeat',
        type,
        mode: 'until',
        condition: {
          match: 'all',
          rules: [{ left: '{{iteration}}', operator: 'greater_or_equal', right: '3' }],
        },
        step: a,
        maxIterations: 10,
      };
    }
    case 'foreach': {
      const a = inner('{{item}}');
      return {
        id,
        name: 'For each item',
        type,
        items: '{{prev}}',
        step: a,
        concurrency: 1,
      };
    }
    default:
      throw new Error(`Unknown entry ${type}`);
  }
}

/** Replaces the entry or step with `id` (anywhere in the graph). */
export function replaceById(
  def: WorkflowDefinition,
  id: string,
  next: WorkflowEntry | WorkflowStep,
): WorkflowDefinition {
  const swap = <T extends { id: string }>(s: T): T => (s.id === id ? (next as unknown as T) : s);
  return {
    ...def,
    graph: def.graph.map((entry) => {
      if (entry.id === id) return next as WorkflowEntry;
      switch (entry.type) {
        case 'parallel':
          return { ...entry, steps: entry.steps.map(swap) };
        case 'conditional':
          return {
            ...entry,
            branches: entry.branches.map((b) => ({ ...b, step: swap(b.step) })),
            ...(entry.fallback ? { fallback: swap(entry.fallback) } : {}),
          };
        case 'loop':
        case 'foreach':
          return { ...entry, step: swap(entry.step) };
        default:
          return entry;
      }
    }),
  };
}

/** The leaf step or entry with `id`, the top-level entry holding it and its index. */
export function locate(def: WorkflowDefinition, id: string) {
  for (const [index, entry] of def.graph.entries()) {
    if (entry.id === id) return { index, entry, node: entry as WorkflowEntry | WorkflowStep };
    const child = isLeaf(entry) ? undefined : entrySteps(entry).find((s) => s.id === id);
    if (child) return { index, entry, node: child as WorkflowEntry | WorkflowStep };
  }
  return null;
}

/** Live state of one step while a run streams (merged over the stored results). */
export interface LiveStep {
  status: WorkflowStepStatus;
  input?: unknown;
  output?: unknown;
  error?: string | null;
  text?: string;
  runId?: string | null;
  childRunId?: string | null;
  iterations?: number;
  items?: { total: number; done: number };
  resumeAt?: string;
  message?: string;
}

export function fromResults(results: WorkflowStepResult[]): Record<string, LiveStep> {
  return Object.fromEntries(
    results.map((r) => [
      r.stepId,
      {
        status: r.status,
        input: r.input,
        output: r.output,
        error: r.error,
        runId: r.runId,
        childRunId: r.childRunId ?? null,
        ...(r.iterations !== undefined ? { iterations: r.iterations } : {}),
        ...(r.items ? { items: r.items } : {}),
        ...(r.resumeAt ? { resumeAt: r.resumeAt } : {}),
        ...(r.message ? { message: r.message } : {}),
      },
    ]),
  );
}

/** Applies one streamed event to the live step map. */
export function applyEvent(
  steps: Record<string, LiveStep>,
  event: WorkflowEventDto,
): Record<string, LiveStep> {
  if (!('stepId' in event)) return steps;
  const prev = steps[event.stepId] ?? { status: 'pending' as const };
  let next: LiveStep = prev;
  switch (event.type) {
    case 'step.started':
      next = { ...prev, status: 'running', input: event.input, error: null, text: '' };
      break;
    case 'step.delta':
      next = { ...prev, text: (prev.text ?? '') + event.text };
      break;
    case 'step.progress':
      next = {
        ...prev,
        ...(event.iterations !== undefined ? { iterations: event.iterations } : {}),
        ...(event.items ? { items: event.items } : {}),
      };
      break;
    case 'step.completed':
      next = { ...prev, status: 'succeeded', output: event.output, runId: event.runId };
      break;
    case 'step.skipped':
      next = { ...prev, status: 'skipped' };
      break;
    case 'step.failed':
      next = { ...prev, status: 'failed', error: event.error, runId: event.runId };
      break;
    case 'step.suspended':
      next = { ...prev, status: 'suspended', message: event.message };
      break;
    case 'step.waiting':
      next = { ...prev, status: 'waiting', resumeAt: event.resumeAt };
      break;
  }
  return { ...steps, [event.stepId]: next };
}

export const formatValue = (value: unknown) =>
  typeof value === 'string' ? value : JSON.stringify(value, null, 2);

/** "1h 30m", "45s", "2d" from milliseconds. */
export function formatSleep(ms: number): string {
  const parts: string[] = [];
  const units: Array<[string, number]> = [
    ['d', 86_400_000],
    ['h', 3_600_000],
    ['m', 60_000],
    ['s', 1000],
  ];
  let rest = ms;
  for (const [unit, size] of units) {
    const n = Math.floor(rest / size);
    if (n > 0) parts.push(`${n}${unit}`);
    rest -= n * size;
  }
  return parts.slice(0, 2).join(' ') || '0s';
}

export const STARTER: WorkflowDefinition = {
  version: 2,
  inputs: [{ name: 'topic', type: 'string', required: true, description: 'What to write about' }],
  graph: [
    {
      id: 'brief',
      name: 'Build the brief',
      type: 'transform',
      output: 'Write a short, friendly paragraph about {{input.topic}}.',
    },
  ],
};
